import React, { createContext, useContext, useState, useEffect } from 'react';
import { clearMediaToken } from '../lib/mediaToken';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
};

// Remove this user's cached API responses and offline queue (shared-device safety)
const clearOfflineUserData = () => {
    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_USER_DATA' });
    }
    if (window.caches) {
        window.caches.keys().then((names) => names.forEach((name) => {
            window.caches.open(name).then((cache) => cache.keys().then((reqs) => reqs
                .filter((r) => new URL(r.url).pathname.startsWith('/api/'))
                .forEach((r) => cache.delete(r))));
        })).catch(() => {});
    }
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        // Check for saved user in localStorage (for quick initial render)
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
            try {
                const parsed = JSON.parse(savedUser);
                // Guest sessions: restore from localStorage only (no /auth/me)
                if (parsed.role === 'guest') {
                    setUser(parsed);
                    setLoading(false);
                    return;
                }
            } catch (e) {
                localStorage.removeItem('user');
            }
        }

        // Verify session with backend (cookie-based auth)
        const token = localStorage.getItem('token');
        if (token || savedUser) {
            try {
                const res = await authAPI.getMe();
                setUser(res.data);
                localStorage.setItem('user', JSON.stringify(res.data));
            } catch (error) {
                // Cookie/token invalid — clear local state
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                setUser(null);
            }
        }
        setLoading(false);
    };

    const login = async (email, password) => {
        const res = await authAPI.login({ email, password });
        const { token, user: userData } = res.data;
        // Keep token in localStorage as fallback for WebSocket
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        return res.data;
    };

    const register = async (data) => {
        const res = await authAPI.register(data);
        return res.data;
    };

    const registerLeader = async (data) => {
        const res = await authAPI.registerLeader(data);
        const { token, user: userData } = res.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        return res.data;
    };

    const guestLogin = async () => {
        const res = await authAPI.guest();
        const { token, user: userData } = res.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        return res.data;
    };

    const logout = async () => {
        try {
            await authAPI.logout();
        } catch (err) {
            // Cookie may already be expired — log for debugging
            if (process.env.NODE_ENV === 'development') console.warn('Logout API call failed:', err.message);
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        clearOfflineUserData();
        clearMediaToken();
        setUser(null);
    };

    const refreshUser = async () => {
        try {
            const res = await authAPI.getMe();
            setUser(res.data);
            localStorage.setItem('user', JSON.stringify(res.data));
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.warn('Failed to refresh user:', err.message);
        }
    };

    const isAuthenticated = !!user;
    const isApproved = user?.is_approved;
    const isAdmin = user?.role === 'admin';
    const isTeacher = user?.role === 'teacher';
    const isTeacherOrAdmin = user?.role === 'teacher' || user?.role === 'admin';
    const isGuest = user?.role === 'guest';
    const needsGroupSetup = !!user?.needs_group_setup;

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            login,
            register,
            registerLeader,
            guestLogin,
            logout,
            refreshUser,
            isAuthenticated,
            isApproved,
            isAdmin,
            isTeacher,
            isTeacherOrAdmin,
            isGuest,
            needsGroupSetup,
        }}>
            {children}
        </AuthContext.Provider>
    );
};
